import { Modal, Form, InputNumber, Input } from "antd"
import { FC, useEffect } from "react"
import { SubmitCancelButtons, RaffleSelector } from "components"
import {
  createContestPeriodGame,
  updateContestPeriodGame,
  CreateContestPeriodGameRequest,
  UpdateContestPeriodGameRequest,
  ContestPeriodGame,
} from "../../../api"
import { notification } from "antd"
import { useStore } from "stores/utils/use-store"
import { ContestConfig } from "domains/managingJson/store/types"

interface GameModalProps {
  visible: boolean;
  onClose: () => void;
  editType: "create" | "edit";
  contestPeriodId: string;
  editingGame?: ContestPeriodGame | null;
  loading: boolean;
  onSuccess: () => void;
  singleRaffleId?: string;
  isSingleMode?: boolean;
}

export const GameModal: FC<GameModalProps> = ({
  visible,
  onClose,
  editType,
  contestPeriodId,
  editingGame,
  loading,
  onSuccess,
  singleRaffleId,
  isSingleMode,
}) => {
  const [form] = Form.useForm()
  const { dataStore: { managingJsonStore: { selectedElement } } } = useStore()
  const contestData = selectedElement.data as ContestConfig
  const isMappedMode = contestData.isMappedRaffleMode || false

  useEffect(() => {
    if (editType === "edit" && editingGame && visible) {
      form.setFieldsValue({
        gameId: editingGame.gameId,
        prizeBuffPoints: editingGame.prizeBuffPoints,
        prizeGiftCard: editingGame.prizeGiftCard,
        prizesCount: editingGame.prizesCount,
        raffleId: editingGame.raffleId,
      })
    } else if (editType === "create" && isSingleMode && singleRaffleId) {
      // Auto-set raffle ID in single mode for new games
      form.setFieldsValue({ raffleId: singleRaffleId })
    }
  }, [editType, editingGame, visible, form, isSingleMode, singleRaffleId])

  const handleSubmit = async(values: {
    gameId: number;
    prizeBuffPoints: number;
    prizeGiftCard: string;
    prizesCount: number;
    raffleId?: string;
  }) => {
    try {
      // In single mode, always use the single raffle ID
      const raffleToUse = isSingleMode ? singleRaffleId : values.raffleId

      if (editType === "create") {
        const requestData: CreateContestPeriodGameRequest = {
          gameId: values.gameId,
          prizeBuffPoints: values.prizeBuffPoints,
          prizeGiftCard: values.prizeGiftCard || null,
          prizesCount: values.prizesCount,
          raffleId: raffleToUse,
        }

        await createContestPeriodGame(contestPeriodId, requestData)
        notification.success({ message: "Game created successfully" })
      } else {
        if (!editingGame) return
        const requestData: UpdateContestPeriodGameRequest = {
          prizeBuffPoints: values.prizeBuffPoints,
          prizeGiftCard: values.prizeGiftCard || null,
          prizesCount: values.prizesCount,
          raffleId: raffleToUse,
        }

        await updateContestPeriodGame(contestPeriodId, editingGame.gameId, requestData)
        notification.success({ message: "Game updated successfully" })
      }

      onClose()
      form.resetFields()
      onSuccess()
    } catch (error) {
      console.error(`Failed to ${editType} game:`, error)
      notification.error({ message: `Failed to ${editType} game` })
    }
  }

  const handleClose = () => {
    onClose()
    form.resetFields()
  }

  return (
    <Modal
      visible={visible}
      title={editType === "create" ? "Create Game" : "Edit Game"}
      onCancel={handleClose}
      footer={null}
      width={600}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="Game ID"
          name="gameId"
          rules={[{ required: true, message: "Please enter game ID" }]}
        >
          <InputNumber min={1} style={{ width: "100%" }} disabled={editType === "edit"} />
        </Form.Item>

        <Form.Item
          label="Prize Buff Points"
          name="prizeBuffPoints"
          rules={[{ required: true, message: "Please enter prize buff points" }]}
        >
          <InputNumber min={0} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          label="Prize Gift Card"
          name="prizeGiftCard"
        >
          <Input placeholder="Gift card prize (optional)" />
        </Form.Item>

        <Form.Item
          label="Prizes Count"
          name="prizesCount"
          rules={[{ required: true, message: "Please enter prizes count" }]}
        >
          <InputNumber min={1} style={{ width: "100%" }} />
        </Form.Item>

        {(isMappedMode || (!isMappedMode && editType === "edit")) && (
          <Form.Item
            label="Raffle"
            name="raffleId"
            rules={[{ required: true, message: "Please select a raffle" }]}
          >
            <RaffleSelector disabled={!isMappedMode} />
          </Form.Item>
        )}

        <SubmitCancelButtons onClose={handleClose} isLoading={loading} />
      </Form>
    </Modal>
  )
}
